// Login Component for AAR Guest Management System
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginWithEmail, loginWithMicrosoft, resetPassword } from '../firebase/authService';
import '../styles/Login.css';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const navigate = useNavigate();

  // Refs for managing floating labels
  const emailFieldRef = useRef(null);
  const passwordFieldRef = useRef(null);

  // Pre-fill email from URL query parameter (from email verification)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const verifiedParam = searchParams.get('verified');

    if (emailParam) {
      setEmail(emailParam);
    }

    if (verifiedParam === 'true') {
      setSuccess('Email verified successfully! You can now log in with your account.');
    }
  }, [searchParams]);

  // Clear messages after timeout
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await loginWithEmail(email, password);

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        setError('Please verify your email address before logging in. Check your email for the verification link.');
        setLoading(false);
        // Sign out the unverified user
        await userCredential.user.auth.signOut();
        return;
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);

      // User-friendly error messages
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact an administrator.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed login attempts. Please try again later.');
          break;
        default:
          setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address above, then click "Forgot password?" again.');
      return;
    }

    setResetSending(true);
    try {
      await resetPassword(trimmed);
      setSuccess(`If an account exists for ${trimmed}, a password reset link has been sent. Check your inbox (and spam folder).`);
    } catch (error) {
      console.error('Password reset error:', error);
      switch (error.code) {
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please wait a few minutes and try again.');
          break;
        case 'auth/user-not-found':
          // Don't reveal whether the email exists — show generic success.
          setSuccess(`If an account exists for ${trimmed}, a password reset link has been sent. Check your inbox (and spam folder).`);
          break;
        default:
          setError('Could not send password reset email. Please try again or contact an administrator.');
      }
    } finally {
      setResetSending(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await loginWithMicrosoft();
      navigate('/dashboard');
    } catch (error) {
      console.error('Microsoft login error:', error);

      if (error.message === 'NOT_AUTHORIZED' || error.message === 'GROUP_MEMBERSHIP_REQUIRED') {
        setError('Access denied. You do not have permission to access this application. Please contact your administrator.');
        setLoading(false);
        return;
      }

      switch (error.code) {
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
          setError('Sign-in was cancelled.');
          break;
        case 'auth/popup-blocked':
          setError('Sign-in popup was blocked. Please allow popups and try again.');
          break;
        case 'auth/unauthorized-domain':
          setError('This domain is not authorized for Microsoft sign-in.');
          break;
        case 'auth/account-exists-with-different-credential':
          setError('An account already exists with the same email but different sign-in credentials.');
          break;
        default: {
          const detail = error.code || error.message || 'unknown error';
          setError(`Microsoft sign-in failed: ${detail}. Please try again or contact an administrator.`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Focus and blur handlers for styling
  const handleFocus = (fieldRef) => {
    if (fieldRef.current) {
      fieldRef.current.classList.add('focused');
    }
  };

  const handleBlur = (fieldRef) => {
    if (fieldRef.current) {
      fieldRef.current.classList.remove('focused');
    }
  };

  // Eye icon SVG component
  const EyeIcon = ({ show }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {show ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      )}
    </svg>
  );

  return (
    <div className="login-wrapper">
      <div className="login-container">
        {/* Logo/Header Section */}
        <div className="login-header">
          <div className="login-logo">
            <img src="/bt_logo.jpg" alt="Brooklyn Tabernacle Logo" />
          </div>
          <h1>AAR Guest Mgmt</h1>
          <p className="login-subtitle">Sign in to AAR Guest Mgmt. System</p>
        </div>

        {/* Microsoft Sign-In */}
        <button
          onClick={handleMicrosoftLogin}
          disabled={loading}
          className="microsoft-signin-btn"
        >
          {loading ? (
            <div className="spinner-inline"></div>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 20 20">
                <path fill="#f25022" d="M0 0h9.5v9.5H0z"/>
                <path fill="#00a4ef" d="M10.5 0H20v9.5h-9.5z"/>
                <path fill="#7fba00" d="M0 10.5h9.5V20H0z"/>
                <path fill="#ffb900" d="M10.5 10.5H20V20h-9.5z"/>
              </svg>
              <span>BT Staff Login</span>
            </>
          )}
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* Email Sign-In Form */}
        <form onSubmit={handleEmailLogin} className="email-auth-form">
          <div className="form-field" ref={emailFieldRef}>
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => handleFocus(emailFieldRef)}
              onBlur={() => handleBlur(emailFieldRef)}
              required
              autoComplete="email"
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-field password-field" ref={passwordFieldRef}>
            <label htmlFor="password">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => handleFocus(passwordFieldRef)}
              onBlur={() => handleBlur(passwordFieldRef)}
              required
              minLength={6}
              autoComplete="current-password"
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex="-1"
            >
              <EyeIcon show={showPassword} />
            </button>
          </div>

          <div className="forgot-password-row">
            <button
              type="button"
              className="forgot-password-link"
              onClick={handleForgotPassword}
              disabled={resetSending || loading}
            >
              {resetSending ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? <div className="spinner-inline"></div> : 'Sign In'}
          </button>
        </form>

        {/* Messages */}
        {error && (
          <div className="message error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="message success-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>{success}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
